import posthog from 'posthog-js';
import type { AdhesionMode } from '../api/users/getAdhesionDetails';
import type { ContributionAdjustmentMethod } from '../constants/contributionAdjustmentMethods';

// Mismos valores que User.getAccountType() en comparsa. Cuando el enum
// AccountType de src/types/dashboard.ts se mergee desde la rama
// feat/personal-view-for-b2b, sustituir esta constante por el import.
export const ACCOUNT_TYPE = {
	ADMIN: 'ADMIN',
	USER: 'USER',
	MANAGEMENT: 'MANAGEMENT',
	BOTH: 'BOTH'
} as const;
export type AccountType = (typeof ACCOUNT_TYPE)[keyof typeof ACCOUNT_TYPE];

export const ANALYTICS_EVENTS = {
	USER_LOGGED_IN: 'user_logged_in',
	CONTRIBUTION_CONFIGURED: 'contribution_configured',
	VOLUNTARY_EXIT_STARTED: 'voluntary_exit_started',
	TRANSFER_PLAN_STARTED: 'transfer_plan_started',
	HHRR_SOFTWARE_NOT_AVAILABLE: 'hhrr_software_not_available',
	PAYROLL_SIMULATOR_INTEREST: 'payroll_simulator_interest',
	TAXDOWN_PARTNERSHIP_CLICK: 'taxdown_partnership_click',
	REFERRAL_MODAL_OPENED: 'referral_modal_opened',
	REFERRAL_LINK_COPIED: 'referral_link_copied',
	REFERRAL_MESSAGE_COPIED: 'referral_message_copied',
	REFERRAL_LINK_SHARED: 'referral_link_shared',
	REFERRALS_PAGE_VIEWED: 'referrals_page_viewed',
	// TODO: eliminar si ya no se usan en otras partes
	REFERRAL_INVITE_OPENED: 'referral_invite_opened',
	REFERRAL_INVITE_SENT: 'referral_invite_sent',
	BONUS_ACTION_CLICK: 'bonus_action_click',
	SYNC_EMPLOYEES_MODAL_OPENED: 'sync_employees_modal_opened',
	CONTRIBUTION_MODAL_OPENED: 'contribution_modal_opened',
	CONTRIBUTION_UPDATED: 'contribution_updated',
	TRANSFER_PLAN_PAGE_VIEWED: 'transfer_plan_page_viewed',
	TRANSFER_PLAN_FUND_SELECTED: 'transfer_plan_fund_selected',
	TRANSFER_PLAN_COMPLETED: 'transfer_plan_completed',
	TRANSFER_PLAN_ABANDONED: 'transfer_plan_abandoned',
	EXTERNAL_PLAN_PANEL_OPENED: 'external_plan_panel_opened',
	EXTERNAL_PLAN_SAVED: 'external_plan_saved',
	EXTERNAL_PLAN_PANEL_CLOSED_WITHOUT_SAVE: 'external_plan_panel_closed_without_save',
	EXTERNAL_PLAN_AUTO_SKIPPED: 'external_plan_auto_skipped'
} as const;

export type PosthogRole = 'admin' | 'employee';

// Mapea el accountType del backend al rol que usan los dashboards de
// PostHog para filtrar admins vs empleados.
export const accountTypeToRole = (accountType?: AccountType | string): PosthogRole | undefined => {
	switch (accountType) {
		case ACCOUNT_TYPE.ADMIN:
		case ACCOUNT_TYPE.MANAGEMENT:
		case ACCOUNT_TYPE.BOTH:
			return 'admin';
		case ACCOUNT_TYPE.USER:
			return 'employee';
		default:
			return undefined;
	}
};

const extractDomain = (email?: string): string | undefined => {
	if (!email || !email.includes('@')) return undefined;
	return email.split('@')[1].toLowerCase();
};

interface IdentifyProps {
	id: string;
	companyId?: string;
	accountType?: AccountType | string;
	email?: string;
	companyName?: string;
	language?: string;
}

interface UserProperties {
	email?: string;
	name?: string;
	isFreelance?: boolean;
	isIndividual?: boolean;
	accountType?: AccountType | string;
	companyName?: string;
	language?: string;
}

/**
 * Tras una impersonación el navegador queda sin registrar (`startImpersonation`). Un login de
 * verdad o un logout lo vuelven a activar, sin mandar el evento `$opt_in`.
 *
 * 🔴 Primero el opt-in y DESPUÉS el reset. Mientras está en opt-out PostHog no guarda nada,
 * tampoco el reset de la impersonación: al volver a registrar recupera la identidad que había
 * antes de impersonar. El reset tiene que ocurrir ya con el registro activo para que la
 * identidad nueva se guarde. Devuelve si había que reactivar.
 */
const resumeTracking = (): boolean => {
	if (!posthog.has_opted_out_capturing()) return false;
	posthog.opt_in_capturing({ captureEventName: false });
	posthog.reset();
	return true;
};

export const analytics = {
	identify(props: IdentifyProps) {
		resumeTracking();
		posthog.identify(props.id, {
			company_id: props.companyId,
			account_type: props.accountType,
			role: accountTypeToRole(props.accountType),
			company_domain: extractDomain(props.email),
			company_name: props.companyName,
			language: props.language
		});
	},

	setUserProperties(props: UserProperties) {
		posthog.people.set({
			email: props.email,
			name: props.name,
			is_freelance: props.isFreelance,
			is_individual: props.isIndividual,
			role: accountTypeToRole(props.accountType),
			company_domain: extractDomain(props.email),
			company_name: props.companyName,
			language: props.language
		});
	},

	// Sincroniza la person property `language` cuando el usuario cambia el
	// idioma en ajustes, para que el filtrado de surveys quede al día sin
	// re-identificar al usuario completo.
	setLanguage(language: string) {
		posthog.people.set({ language });
	},

	reset() {
		// Tras una impersonación, `resumeTracking` ya hace el reset (después del opt-in).
		if (!resumeTracking()) posthog.reset();
	},

	/**
	 * Impersonación desde cuarteto: no se registra nada, ni eventos ni replays. El token es el
	 * del usuario, así que identificarlo haría pasar al empleado por el cliente y PostHog
	 * fusionaría en el cliente el historial de ese navegador. Antes se suelta la identidad
	 * que hubiera, de una sesión o impersonación anterior.
	 */
	startImpersonation() {
		posthog.reset();
		posthog.opt_out_capturing();
	},

	track(event: string, properties?: Record<string, unknown>) {
		posthog.capture(event, properties);
	},

	userLoggedIn(props: { accountType: string; companyId: string }) {
		posthog.capture(ANALYTICS_EVENTS.USER_LOGGED_IN, {
			account_type: props.accountType,
			company_id: props.companyId
		});
	},

	contributionConfigured(props: { amount: number; contributeAlwaysMax: boolean }) {
		posthog.capture(ANALYTICS_EVENTS.CONTRIBUTION_CONFIGURED, {
			amount: props.amount,
			contribute_always_max: props.contributeAlwaysMax
		});
	},

	voluntaryExitStarted() {
		posthog.capture(ANALYTICS_EVENTS.VOLUNTARY_EXIT_STARTED);
	},

	transferPlanStarted(props: { dgsfpCode: string }) {
		posthog.capture(ANALYTICS_EVENTS.TRANSFER_PLAN_STARTED, {
			dgsfp_code: props.dgsfpCode
		});
	},

	contributionModalOpened(props: {
		sourcePage: string;
		/** Lo abrió el enlace del correo (chirigota#630). */
		fromEmail?: boolean;
	}) {
		posthog.capture(ANALYTICS_EVENTS.CONTRIBUTION_MODAL_OPENED, {
			source_page: props.sourcePage,
			...(props.fromEmail ? { from_email: true } : {})
		});
	},

	contributionUpdated(props: {
		previousAmount: number;
		newAmount: number;
		adjustmentMethod?: ContributionAdjustmentMethod;
		/** El plan que se ha cambiado: el de empleo o uno personal. */
		mode?: AdhesionMode;
	}) {
		posthog.capture(ANALYTICS_EVENTS.CONTRIBUTION_UPDATED, {
			previous_amount: props.previousAmount,
			new_amount: props.newAmount,
			...(props.adjustmentMethod ? { adjustment_method: props.adjustmentMethod } : {}),
			...(props.mode ? { mode: props.mode } : {})
		});
	},

	transferPlanPageViewed(props: { currentUrl: string }) {
		posthog.capture(ANALYTICS_EVENTS.TRANSFER_PLAN_PAGE_VIEWED, {
			current_url: props.currentUrl
		});
	},

	transferPlanFundSelected(props: { fundName: string }) {
		posthog.capture(ANALYTICS_EVENTS.TRANSFER_PLAN_FUND_SELECTED, {
			fund_name: props.fundName
		});
	},

	transferPlanCompleted(props: { fundSelected: string; dgsfpCode: string }) {
		posthog.capture(ANALYTICS_EVENTS.TRANSFER_PLAN_COMPLETED, {
			fund_selected: props.fundSelected,
			dgsfp_code: props.dgsfpCode
		});
	},

	transferPlanAbandoned(props: { stepAtAbandon: number }) {
		posthog.capture(ANALYTICS_EVENTS.TRANSFER_PLAN_ABANDONED, {
			step_at_abandon: props.stepAtAbandon
		});
	},

	externalPlanPanelOpened() {
		posthog.capture(ANALYTICS_EVENTS.EXTERNAL_PLAN_PANEL_OPENED);
	},

	externalPlanSaved(props: { planType: string }) {
		posthog.capture(ANALYTICS_EVENTS.EXTERNAL_PLAN_SAVED, {
			plan_type: props.planType
		});
	},

	externalPlanPanelClosedWithoutSave() {
		posthog.capture(ANALYTICS_EVENTS.EXTERNAL_PLAN_PANEL_CLOSED_WITHOUT_SAVE);
	},

	externalPlanAutoSkipped() {
		posthog.capture(ANALYTICS_EVENTS.EXTERNAL_PLAN_AUTO_SKIPPED);
	}
};
